/**
 * Telemetry for the last mile of a workflow run: getting its RESULT onto the
 * user's screen.
 *
 * Why this exists as its own concern. Workflows are asynchronous —
 * `workflow_run` returns in ~25ms and the run then executes for minutes — so
 * the chat turn that started the run narrates, ends, and closes its SSE stream
 * long before the run finishes. The result is delivered later by a completely
 * different path:
 *
 *   engine finishes  →  ChatTranscriptSink writes the session transcript (S3)
 *                    →  useWorkflowRunProgress polls, sees running → terminal
 *                    →  AgentConsole waits 6s, calls sessions.reloadCurrent()
 *                    →  the transcript renders in chat
 *
 * Every link there could fail silently, and the whole chain was unobservable:
 * 30 days of production `turn.stream` records showed 2,664 turns with ZERO
 * disconnects and ZERO missing terminal frames, because the stream is healthy
 * and irrelevant by the time delivery happens. Stream telemetry cannot see this
 * class of failure at all — which is why it gets its own event.
 *
 * `resultInTranscript` is the field that answers the user's actual question.
 * `refreshed: true` only says we re-pulled the transcript; it does not say the
 * run's result was IN it. A run whose server-side sink never wrote produces
 * `refreshed: true, resultInTranscript: false`, and that pair is the reported
 * bug — distinguishable from "we never refreshed at all", which is a client
 * bug, only because both fields are recorded.
 */

import { recordEvent } from '../../telemetry/rum.ts';
import type { SessionTranscriptTurn } from '../../agents/sessionApi.ts';
import type { ReloadOutcome } from './useSessions.ts';

/** Identity of the finished run whose delivery is being reported. */
export interface WorkflowDeliveryEvent {
  readonly runId: string;
  readonly workflowId: string;
  /** Terminal status observed by the progress poller. */
  readonly status: string;
  /** Set when the refresh never ran, and why. */
  readonly skipped?: 'session-switched';
}

/** A refresh held back because a turn was streaming when the run finished. */
export interface PendingRunReload {
  /** The session the run belongs to; a switch invalidates the refresh. */
  readonly sessionId: string;
  readonly event: WorkflowDeliveryEvent;
}

/**
 * Did the run's completion turn actually land in the restored transcript?
 *
 * The server-side sink writes the turn with a deterministic prefix — see
 * `describeOutcome` in the agents runtime's `workflows/completion/chat-sink.ts`,
 * which emits `Your workflow "<id>" finished.` on success and
 * `Your workflow "<id>" did not finish: …` otherwise, alongside a user-role
 * turn reading `(workflow "<id>" finished)`.
 *
 * Matching on `workflow "<id>"` covers all three, and matching the id rather
 * than the surrounding prose keeps this from breaking the moment the copy is
 * reworded — the id is the part that is a contract.
 */
export function transcriptHasRunResult(
  turns: readonly SessionTranscriptTurn[],
  workflowId: string,
): boolean {
  if (workflowId === '') {
    return false;
  }
  const needle = `workflow "${workflowId}"`;
  return turns.some((turn) => turn.text.includes(needle));
}

/** Emit one delivery record. Never throws — telemetry must not break delivery. */
export function recordWorkflowDelivery(
  event: WorkflowDeliveryEvent & {
    readonly refreshed?: boolean;
    readonly resultInTranscript?: boolean;
  },
): void {
  recordEvent('om_workflow_delivery', {
    runId: event.runId,
    workflowId: event.workflowId,
    status: event.status,
    refreshed: event.refreshed ?? false,
    resultInTranscript: event.resultInTranscript ?? false,
    ...(event.skipped !== undefined ? { skipped: event.skipped } : {}),
  });
}

/**
 * Refresh the transcript for a finished run and record what the user ended up
 * seeing. The `reload` callback is injected rather than imported so this stays
 * testable without a React tree, and so the module depends on the contract
 * (`ReloadOutcome`) rather than on `useSessions`' implementation.
 */
export async function deliverRunResult(
  event: WorkflowDeliveryEvent,
  reload: () => Promise<ReloadOutcome>,
): Promise<void> {
  const outcome = await reload();
  recordWorkflowDelivery({
    ...event,
    refreshed: outcome.ok,
    resultInTranscript: outcome.ok && transcriptHasRunResult(outcome.turns, event.workflowId),
  });
}
